/**
 * TradetrON Settings Page
 * Configure bi-directional TradetrON integration
 *
 * Version: 6.2.0
 */
import React, { useState, useEffect } from 'react'
import { Link2, Settings, RefreshCw, CheckCircle, XCircle, ExternalLink, Copy, Eye, EyeOff, Zap, Shield, Bell } from 'lucide-react'
import './TradetrONSettings.css'

// Types
interface TradetrONConfig {
  enabled: boolean
  apiKey: string
  apiSecret: string
  webhookUrl: string
  signals: {
    buy: boolean
    sell: boolean
    short: boolean
    cover: boolean
  }
  autoExecute: boolean
  notifyOnSignal: boolean
  lastSync: string | null
  status: 'connected' | 'disconnected' | 'error'
}

const TradetrONSettings: React.FC = () => {
  const [config, setConfig] = useState<TradetrONConfig>({
    enabled: false,
    apiKey: '',
    apiSecret: '',
    webhookUrl: '',
    signals: {
      buy: true,
      sell: true,
      short: true,
      cover: true
    },
    autoExecute: false,
    notifyOnSignal: true,
    lastSync: null,
    status: 'disconnected'
  })
  const [showSecret, setShowSecret] = useState(false)
  const [saving, setSaving] = useState(false)
  const [testResult, setTestResult] = useState<'success' | 'error' | null>(null)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    // Load saved config
    const saved = localStorage.getItem('tradetron-config')
    if (saved) {
      try {
        const parsed = JSON.parse(saved)
        setConfig({ ...parsed, apiSecret: '' }) // Don't show secret
      } catch (e) {
        console.error('Failed to load config')
      }
    }
  }, [])

  const handleSave = () => {
    setSaving(true)
    setTimeout(() => {
      localStorage.setItem('tradetron-config', JSON.stringify(config))
      setSaving(false)
    }, 1000)
  }

  const handleTestConnection = () => {
    setTestResult(null)
    setTimeout(() => {
      // Simulate test
      if (config.apiKey && config.apiSecret) {
        setTestResult('success')
        setConfig(prev => ({ ...prev, status: 'connected', lastSync: new Date().toISOString() }))
      } else {
        setTestResult('error')
        setConfig(prev => ({ ...prev, status: 'error' }))
      }
    }, 1500)
  }

  const handleCopyWebhook = () => {
    const webhookUrl = `${window.location.origin}/api/webhook/tradetron`
    navigator.clipboard.writeText(webhookUrl)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const generateWebhookUrl = () => {
    const randomId = Math.random().toString(36).substring(2, 15)
    const webhookUrl = `${window.location.origin}/api/webhook/tradetron/${randomId}`
    setConfig(prev => ({ ...prev, webhookUrl }))
    return webhookUrl
  }

  return (
    <div className="tradetron-settings-page">
      <div className="page-header">
        <div className="header-content">
          <h1><Link2 className="page-icon" /> TradetrON Integration</h1>
          <p className="page-subtitle">Configure bi-directional trading with TradetrON</p>
        </div>
        <div className={`status-badge ${config.status}`}>
          {config.status === 'connected' && <CheckCircle size={16} />}
          {config.status === 'disconnected' && <XCircle size={16} />}
          {config.status === 'error' && <XCircle size={16} />}
          <span>{config.status.charAt(0).toUpperCase() + config.status.slice(1)}</span>
        </div>
      </div>

      {/* Signal Types Info */}
      <div className="info-card">
        <div className="info-header">
          <Zap size={20} />
          <h3>Signal Values Reference</h3>
        </div>
        <div className="signals-grid">
          <div className="signal-item buy">
            <span className="signal-value">1</span>
            <span className="signal-label">BUY - Enter Long</span>
          </div>
          <div className="signal-item sell">
            <span className="signal-value">2</span>
            <span className="signal-label">SELL - Exit Long</span>
          </div>
          <div className="signal-item short">
            <span className="signal-value">3</span>
            <span className="signal-label">SHORT - Enter Short</span>
          </div>
          <div className="signal-item cover">
            <span className="signal-value">4</span>
            <span className="signal-label">COVER - Exit Short</span>
          </div>
        </div>
      </div>

      {/* Connection Settings */}
      <div className="settings-section">
        <div className="section-header">
          <Settings size={20} />
          <h2>API Credentials</h2>
        </div>
        
        <div className="form-group">
          <label>Enable TradetrON Integration</label>
          <div className="toggle-wrapper">
            <input
              type="checkbox"
              checked={config.enabled}
              onChange={(e) => setConfig(prev => ({ ...prev, enabled: e.target.checked }))}
              className="toggle-input"
            />
            <span className="toggle-slider"></span>
          </div>
        </div>

        <div className="form-group">
          <label>API Key</label>
          <input
            type="text"
            value={config.apiKey}
            onChange={(e) => setConfig(prev => ({ ...prev, apiKey: e.target.value }))}
            placeholder="Enter your TradetrON API Key"
            className="form-input"
          />
        </div>

        <div className="form-group">
          <label>API Secret</label>
          <div className="input-with-icon">
            <input
              type={showSecret ? 'text' : 'password'}
              value={config.apiSecret}
              onChange={(e) => setConfig(prev => ({ ...prev, apiSecret: e.target.value }))}
              placeholder="Enter your TradetrON API Secret"
              className="form-input"
            />
            <button 
              className="icon-btn"
              onClick={() => setShowSecret(!showSecret)}
            >
              {showSecret ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>

        <button 
          className={`btn btn-test ${testResult === 'success' ? 'success' : testResult === 'error' ? 'error' : ''}`}
          onClick={handleTestConnection}
          disabled={!config.apiKey || !config.apiSecret}
        >
          <RefreshCw size={16} className={testResult ? 'spin' : ''} />
          Test Connection
        </button>

        {testResult && (
          <div className={`test-result ${testResult}`}>
            {testResult === 'success' ? (
              <>
                <CheckCircle size={16} />
                Connection successful!
              </>
            ) : (
              <>
                <XCircle size={16} />
                Connection failed. Check your credentials.
              </>
            )}
          </div>
        )}
      </div>

      {/* Webhook Settings */}
      <div className="settings-section">
        <div className="section-header">
          <Zap size={20} />
          <h2>Webhook Configuration</h2>
        </div>

        <div className="webhook-info">
          <p>Configure this webhook URL in TradetrON to receive signals from ZO Bull:</p>
          <div className="webhook-url-box">
            <code>{config.webhookUrl || `${window.location.origin}/api/webhook/tradetron`}</code>
            <button className="icon-btn" onClick={handleCopyWebhook}>
              {copied ? <CheckCircle size={18} /> : <Copy size={18} />}
            </button>
            <button className="icon-btn" onClick={generateWebhookUrl}>
              <RefreshCw size={18} />
            </button>
          </div>
        </div>

        <div className="form-group">
          <label>Custom Webhook URL (from TradetrON)</label>
          <input
            type="url"
            value={config.webhookUrl}
            onChange={(e) => setConfig(prev => ({ ...prev, webhookUrl: e.target.value }))}
            placeholder="https://tradetron.example.com/webhook"
            className="form-input"
          />
          <span className="form-hint">Paste the webhook URL from TradetrON to receive their signals</span>
        </div>
      </div>

      {/* Signal Settings */}
      <div className="settings-section">
        <div className="section-header">
          <Shield size={20} />
          <h2>Signal Mapping</h2>
        </div>

        <p className="section-desc">Choose which signal types to receive from TradetrON:</p>

        <div className="signal-toggles">
          <div className="signal-toggle">
            <div className="toggle-info">
              <span className="signal-badge buy">BUY</span>
              <span>Execute long entries</span>
            </div>
            <div className="toggle-wrapper">
              <input
                type="checkbox"
                checked={config.signals.buy}
                onChange={(e) => setConfig(prev => ({ 
                  ...prev, 
                  signals: { ...prev.signals, buy: e.target.checked }
                }))}
                className="toggle-input"
              />
              <span className="toggle-slider"></span>
            </div>
          </div>

          <div className="signal-toggle">
            <div className="toggle-info">
              <span className="signal-badge sell">SELL</span>
              <span>Execute long exits</span>
            </div>
            <div className="toggle-wrapper">
              <input
                type="checkbox"
                checked={config.signals.sell}
                onChange={(e) => setConfig(prev => ({ 
                  ...prev, 
                  signals: { ...prev.signals, sell: e.target.checked }
                }))}
                className="toggle-input"
              />
              <span className="toggle-slider"></span>
            </div>
          </div>

          <div className="signal-toggle">
            <div className="toggle-info">
              <span className="signal-badge short">SHORT</span>
              <span>Execute short entries</span>
            </div>
            <div className="toggle-wrapper">
              <input
                type="checkbox"
                checked={config.signals.short}
                onChange={(e) => setConfig(prev => ({ 
                  ...prev, 
                  signals: { ...prev.signals, short: e.target.checked }
                }))}
                className="toggle-input"
              />
              <span className="toggle-slider"></span>
            </div>
          </div>

          <div className="signal-toggle">
            <div className="toggle-info">
              <span className="signal-badge cover">COVER</span>
              <span>Execute short exits</span>
            </div>
            <div className="toggle-wrapper">
              <input
                type="checkbox"
                checked={config.signals.cover}
                onChange={(e) => setConfig(prev => ({ 
                  ...prev, 
                  signals: { ...prev.signals, cover: e.target.checked }
                }))}
                className="toggle-input"
              />
              <span className="toggle-slider"></span>
            </div>
          </div>
        </div>
      </div>

      {/* Execution Settings */}
      <div className="settings-section">
        <div className="section-header">
          <Bell size={20} />
          <h2>Execution & Notifications</h2>
        </div>

        <div className="signal-toggle">
          <div className="toggle-info">
            <span>Auto-Execute Signals</span>
            <span className="form-hint">Automatically execute trades without confirmation</span>
          </div>
          <div className="toggle-wrapper">
            <input
              type="checkbox"
              checked={config.autoExecute}
              onChange={(e) => setConfig(prev => ({ ...prev, autoExecute: e.target.checked }))}
              className="toggle-input"
            />
            <span className="toggle-slider"></span>
          </div>
        </div>

        <div className="signal-toggle">
          <div className="toggle-info">
            <span>Notify on Signal</span>
            <span className="form-hint">Send notification when signal is received</span>
          </div>
          <div className="toggle-wrapper">
            <input
              type="checkbox"
              checked={config.notifyOnSignal}
              onChange={(e) => setConfig(prev => ({ ...prev, notifyOnSignal: e.target.checked }))}
              className="toggle-input"
            />
            <span className="toggle-slider"></span>
          </div>
        </div>
      </div>

      {/* Sync Info */}
      {config.lastSync && (
        <div className="sync-info">
          <CheckCircle size={16} />
          <span>Last synced: {new Date(config.lastSync).toLocaleString()}</span>
        </div>
      )}

      {/* Save Button */}
      <div className="actions">
        <button 
          className="btn btn-primary btn-large"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Configuration'}
        </button>
      </div>

      {/* Help Link */}
      <div className="help-link">
        <a href="/docs/tradetron-integration" target="_blank" rel="noopener noreferrer">
          <ExternalLink size={16} />
          View full TradetrON integration guide
        </a>
      </div>
    </div>
  )
}

export default TradetrONSettings
